import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import type {
  ApplicationSubmission,
  CustomerFeedbackFormData,
} from '../types/applicationForm';
import { EMPTY_CFF_ITEM, EMPTY_CUSTOMER_FEEDBACK_FORM } from '../types/applicationForm';

const TABLE = 'customer_feedback_submissions';
const FORM_SLUG = 'customer-feedback' as const;

function withCffSlug(row: ApplicationSubmission): ApplicationSubmission {
  return {
    ...row,
    form_slug: FORM_SLUG,
    form_data: normalizeCustomerFeedbackFormData(row.form_data),
  };
}

export function normalizeCustomerFeedbackFormData(raw: unknown): CustomerFeedbackFormData {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<CustomerFeedbackFormData>;
  const items = Array.isArray(data.items) ? data.items : [];
  const normalizedItems = Array.from({ length: 5 }, (_, i) => {
    const row = items[i];
    if (!row || typeof row !== 'object') return { ...EMPTY_CFF_ITEM };
    return {
      item_code: String(row.item_code ?? ''),
      quantity: String(row.quantity ?? ''),
      uom: String(row.uom ?? ''),
    };
  });

  const legacyReason =
    typeof (data as { reason?: unknown }).reason === 'string'
      ? String((data as { reason?: string }).reason ?? '')
      : '';
  const firstItemReason =
    Array.isArray(data.items) && data.items[0] && typeof data.items[0] === 'object'
      ? String((data.items[0] as { reason?: string }).reason ?? '')
      : '';

  return {
    ...EMPTY_CUSTOMER_FEEDBACK_FORM,
    ...data,
    items: normalizedItems,
    picker: String(data.picker ?? ''),
    packer: String(data.packer ?? ''),
    driver: String(data.driver ?? ''),
    reason: legacyReason || firstItemReason,
    request_type: (data.request_type ?? '') as CustomerFeedbackFormData['request_type'],
    credit_notes: (data.credit_notes ?? '') as CustomerFeedbackFormData['credit_notes'],
    warehouse_action: (data.warehouse_action ?? '') as CustomerFeedbackFormData['warehouse_action'],
    signature_copy_name: String(data.signature_copy_name ?? ''),
    signature_copy_path: String(data.signature_copy_path ?? ''),
  };
}

export function companyNameFromCff(form: CustomerFeedbackFormData): string {
  return form.company_name.trim() || form.company_code.trim() || 'Customer Feedback';
}

export async function fetchCustomerFeedbackSubmissions(): Promise<ApplicationSubmission[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from(TABLE)
    .select('*')
    .order('application_no', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => withCffSlug(row as ApplicationSubmission));
}

/** Normalized invoice key for duplicate checks (trim + lower-case). */
export function cffInvoiceKey(invoiceNo: string | null | undefined): string {
  return String(invoiceNo ?? '').trim().toLowerCase();
}

/**
 * NCPAR nos that already use this invoice (any saved/posted submission).
 * Empty invoice → no matches.
 */
export function findCffDuplicateInvoiceNcpars(
  rows: ApplicationSubmission[],
  invoiceNo: string,
  options?: { excludeId?: string; excludeApplicationNo?: string },
): string[] {
  const key = cffInvoiceKey(invoiceNo);
  if (!key) return [];
  return rows
    .filter((row) => {
      if (options?.excludeId && row.id === options.excludeId) return false;
      if (options?.excludeApplicationNo && row.application_no === options.excludeApplicationNo) {
        return false;
      }
      const data = row.form_data as CustomerFeedbackFormData;
      return cffInvoiceKey(data?.invoice_no) === key;
    })
    .map((row) => row.application_no)
    .sort((a, b) => b.localeCompare(a));
}

export async function fetchCustomerFeedbackSubmission(id: string): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.from(TABLE).select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Submission not found.');
  return withCffSlug(data as ApplicationSubmission);
}

export async function submitCustomerFeedback(
  formData: CustomerFeedbackFormData,
  submittedBy?: string,
  submitterEmail?: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizeCustomerFeedbackFormData(formData);
  const { data, error } = await db.rpc('submit_customer_feedback', {
    p_company_name: companyNameFromCff(payload),
    p_form_data: payload,
    p_submitted_by: submittedBy?.trim() || null,
    p_submitter_email: submitterEmail?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withCffSlug(data as ApplicationSubmission);
}

export async function updateCustomerFeedback(
  id: string,
  formData: CustomerFeedbackFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizeCustomerFeedbackFormData(formData);
  const { data, error } = await db.rpc('update_customer_feedback', {
    p_id: id,
    p_company_name: companyNameFromCff(payload),
    p_form_data: payload,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withCffSlug(data as ApplicationSubmission);
}

export async function postCustomerFeedback(
  id: string,
  signatureFileName: string,
  signatureStoragePath: string,
  postedBy?: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('post_customer_feedback', {
    p_id: id,
    p_signature_file_name: signatureFileName.trim(),
    p_signature_storage_path: signatureStoragePath.trim(),
    p_posted_by: postedBy?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withCffSlug(data as ApplicationSubmission);
}

export async function uploadCffSignatureCopy(
  submissionId: string,
  file: File,
): Promise<{ path: string; fileName: string }> {
  const db = getDbSupabase('forms');
  const safeName = file.name.replace(/[^\w.\-()+ ]+/g, '_').slice(0, 120) || 'signature';
  const path = `${submissionId}/${Date.now()}_${safeName}`;
  const { error } = await db.storage.from('cff-signatures').upload(path, file, {
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw new Error(error.message || 'Failed to upload signed copy.');
  return { path, fileName: file.name };
}

export async function downloadCffSignatureCopyBlob(
  storagePath: string,
): Promise<Blob> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.storage.from('cff-signatures').download(storagePath);
  if (error || !data) throw new Error(error?.message || 'Failed to download signed copy.');
  return data;
}

/** Convert uploaded signed copy (PDF or image) to PDF base64 for Teams upload. */
export async function cffSignedCopyToPdfBase64(blob: Blob, fileName: string): Promise<string> {
  const type = blob.type.toLowerCase();
  const lowerName = fileName.toLowerCase();

  const bytesToBase64 = (bytes: Uint8Array): string => {
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  };

  if (type === 'application/pdf' || lowerName.endsWith('.pdf')) {
    return bytesToBase64(new Uint8Array(await blob.arrayBuffer()));
  }

  if (type.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(lowerName)) {
    const { PDFDocument } = await import('pdf-lib');
    const pdf = await PDFDocument.create();
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let image;
    if (type === 'image/png' || lowerName.endsWith('.png')) {
      image = await pdf.embedPng(bytes);
    } else if (type === 'image/jpeg' || /\.jpe?g$/i.test(lowerName)) {
      image = await pdf.embedJpg(bytes);
    } else {
      const bmp = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = bmp.width;
      canvas.height = bmp.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not read signed copy image.');
      ctx.drawImage(bmp, 0, 0);
      const pngBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Failed to convert signed copy image.'))),
          'image/png',
        );
      });
      image = await pdf.embedPng(new Uint8Array(await pngBlob.arrayBuffer()));
    }
    const dims = image.scale(1);
    const page = pdf.addPage([dims.width, dims.height]);
    page.drawImage(image, { x: 0, y: 0, width: dims.width, height: dims.height });
    return bytesToBase64(await pdf.save());
  }

  throw new Error('Signed copy must be a PDF or image (PNG/JPEG/WebP).');
}

export async function cancelCustomerFeedback(
  id: string,
  cancelledBy?: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('cancel_customer_feedback', {
    p_id: id,
    p_cancelled_by: cancelledBy?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withCffSlug(data as ApplicationSubmission);
}

export function isCffCancelled(status: string | undefined | null): boolean {
  return status === 'cancelled';
}

export function isCffPosted(status: string | undefined | null): boolean {
  return status === 'posted';
}

/** Submitted (saved) — editable until posted. */
export function isCffSubmitted(status: string | undefined | null): boolean {
  return status === 'saved';
}

export function isCffLocked(status: string | undefined | null): boolean {
  return status === 'posted' || status === 'cancelled';
}

export type CffWarehouseRolesStatus = 'not_started' | 'not_completed' | 'completed';

/** Picker / Packer / Driver fill status for list badges. */
export function cffWarehouseRolesStatus(
  data: Pick<CustomerFeedbackFormData, 'picker' | 'packer' | 'driver'> | null | undefined,
): CffWarehouseRolesStatus {
  const filled = [data?.picker, data?.packer, data?.driver].filter((v) => String(v ?? '').trim())
    .length;
  if (filled <= 0) return 'not_started';
  if (filled >= 3) return 'completed';
  return 'not_completed';
}

export function cffWarehouseRolesStatusLabel(status: CffWarehouseRolesStatus): string {
  if (status === 'completed') return 'Completed';
  if (status === 'not_completed') return 'Not completed';
  return 'Not Started';
}
